import React, { useState, useEffect } from 'react'
import { GET_FILING_DETAILS, UPDATE_DATA_REQUEST } from 'requests'
import PayrocGenericModal from '../Payroc/PayrocGenericModal'
import DetailsList from 'components/DetailsList/DetailsList'
import ActionButton, { ActionButtonGroup } from 'components/ActionButton/ActionButton'
import DocumentEditIcon from '@tecuity/sos-icons/react/document-edit'
import TrashIcon from '@tecuity/sos-icons/react/trash'
import VisibleIcon from '@tecuity/sos-icons/react/visible'
import PauseIcon from '@tecuity/sos-icons/react/pause'
import UnpauseIcon from '@tecuity/sos-icons/react/play'
import CreditCardIcon from '@tecuity/sos-icons/react/credit-card-solid'
import Portal from 'components/Portal/Portal'
import { Modal, ConfirmDialog } from 'components/Modal/Modal'
import { DumbField } from 'components/Wizard2/Field'
import { TextInput } from 'components/Wizard2/fieldComponents/Form'
import { noop } from 'utilities'
import { history } from '../../store'

const PAYMENT_METHODS = process.env.PAYMENT_METHODS
const PAYROC_METHOD_AVAILABLE = !!PAYMENT_METHODS.find(method => method.type === 'iTransact')
const HIDE_CANCEL_ON_ONETIME_DATA_REQUESTS = process.env.HIDE_CANCEL_ON_ONETIME_DATA_REQUESTS || false

export default ({
  formDefinitionId,
  dataRequestId,
  nickname: initialNickname,
  onNicknameChange,
  frequencyTypeId,
  onCanceled,
  statusId,
  canEdit,
  canPause,
  onPause,
  canUnpause,
  onUnpause,
  canEditPayment
}) => {
  const [details, setDetails] = useState([])
  const [loading, setLoading] = useState(true)
  const [editingNickname, setEditingNickname] = useState(false)
  const [nickname, setNickname] = useState(initialNickname)
  const [confirmingCancel, setConfirmingCancel] = useState(false)
  const [confirmingPause, setConfirmingPause] = useState(false)
  const [confirmingUnpause, setConfirmingUnpause] = useState(false)
  const [canceled, setCanceled] = useState(false)
  const [error, setError] = useState("")
  const [editingPaymentSource, setEditingPaymentSource] = useState(false)

  useEffect(() => {
    setCanceled(false);
    setError("")
    getDetails()
  }, [dataRequestId])

  const getDetails = () => {
    setLoading(true)
    GET_FILING_DETAILS(
      'data-request',
      dataRequestId,
      false,
      ()=>{},
      ({ DRAWER_DETAIL_LIST }) => {
        setDetails(DRAWER_DETAIL_LIST)
        setLoading(false)
      },
      err => {
        console.error(err)
        setLoading(false)
      }
    )
  }

  React.useEffect(() => {
    setNickname(initialNickname)
  }, [dataRequestId])

  const updateNickname = closeModal => {
    setError("")
    UPDATE_DATA_REQUEST(
      {
        DATA_REQUEST_ID: dataRequestId,
        action: 'UPDATE_NAME',
        NAME_OVERRIDE: nickname
      },
      () => {
        onNicknameChange(nickname)
        getDetails()
        closeModal()
      },
      (err) => {
        setLoading(false)
        setError(err.internalerror)
      }
    )
  }

  const cancelSubscription = () => {
    setError("")
    UPDATE_DATA_REQUEST(
      {
        DATA_REQUEST_ID: dataRequestId,
        action: 'CANCEL'
      },
      () => {
        onCanceled()
        setCanceled(true)
        getDetails()
      },
      (err) => {
        setLoading(false)
        setError(err.internalerror)
      }
    )
  }

  const pauseSubscription = () => {
    setError("")
    UPDATE_DATA_REQUEST(
      {
        DATA_REQUEST_ID: dataRequestId,
        action: 'PAUSE'
      },
      () => {
        onPause()
        getDetails()
      },
      (err) => {
        setLoading(false)
        setError(err.internalerror)
      }
    )
  }

  const unpauseSubscription = () => {
    setError("")
    UPDATE_DATA_REQUEST(
      {
        DATA_REQUEST_ID: dataRequestId,
        action: 'UNPAUSE'
      },
      () => {
        onUnpause()
        getDetails()
      },
      (err) => {
        setLoading(false)
        setError(err.internalerror)
      }
    )
  }

  const setPaymentMethod = TOKEN => {
    setEditingPaymentSource(false)
    setError("")
    UPDATE_DATA_REQUEST(
      {
        DATA_REQUEST_ID: dataRequestId,
        TOKEN,
        action: 'UPDATE_PAYMENT'
      },
      () => {
        getDetails()
      },
      err => {
        setLoading(false)
        setError(err.internalerror)
      }
    )
  }

  const toggleNicknameEdit = () => setEditingNickname(x => !x)
  const toggleConfirmingCancel = () => setConfirmingCancel(x => !x)
  const toggleConfirmingPause = () => setConfirmingPause(x => !x)
  const toggleConfirmingUnpause = () => setConfirmingUnpause(x => !x)
  const startReadOnlyWizard = () => {
    history.push(`/view/${formDefinitionId}/${dataRequestId}`)
  }
  const startEditWizard = () => {
    history.push(`/correct/${formDefinitionId}/${dataRequestId}`)
  }

  return (
    <div className={`dr-management-drawer ${loading ? 'loading' : ''}`}>
      <ActionButtonGroup>
        {
          canEdit ?
          <ActionButton
            label="Edit Details"
            children={<DocumentEditIcon />}
            onClick={startEditWizard}
          />
        : null
        }
        {
          canPause ?
            <ActionButton
              label="Pause Subscription"
              children={<PauseIcon />}
              onClick={toggleConfirmingPause}
            />
            : null
        }
        {
          canUnpause ?
            <ActionButton
              label="Unpause Subscription"
              children={<UnpauseIcon />}
              onClick={toggleConfirmingUnpause}
            />
            : null
        }
        {
          canEditPayment ?
          <ActionButton
            label="Select / Add Payment Method"
            children={<CreditCardIcon/>}
            onClick={() => setEditingPaymentSource(true)}
          />
          : null
        }
      </ActionButtonGroup>
      <p className="error-message" aria-live="polite">{error}</p>
      <DetailsList details={details} />
      <ActionButtonGroup>
        <ActionButton
          label="View Details"
          children={<VisibleIcon />}
          onClick={startReadOnlyWizard}
        />
        <ActionButton
          label="Edit Nickname"
          children={<DocumentEditIcon />}
          onClick={toggleNicknameEdit}
        />
        {frequencyTypeId !== 0 && !(HIDE_CANCEL_ON_ONETIME_DATA_REQUESTS && frequencyTypeId === 3500) && statusId === 1071 && !canceled && (
          <ActionButton
            label="Cancel Subscription"
            children={<TrashIcon />}
            onClick={toggleConfirmingCancel}
          />
        )}
      </ActionButtonGroup>
      <Portal isOpened={editingNickname}>
        <Modal
          title="Edit Nickname"
          closeModal={toggleNicknameEdit}
          cancelButton
          saveButton
          autoHeight
          save={updateNickname}
        >
          <NicknameEditor onNicknameChange={setNickname} nickname={nickname} key={dataRequestId} />
        </Modal>
      </Portal>
      <Portal isOpened={confirmingCancel}>
        <ConfirmDialog
          closeModal={toggleConfirmingCancel}
          onModalClosed={noop}
          message="Are you sure you want to cancel this subscription? This cannot be undone."
          buttons={[
            { label: 'No', onClick: noop, className: 'btn-default' },
            {
              label: 'Yes',
              onClick: cancelSubscription,
              className: 'btn-danger'
            }
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      </Portal>
      <Portal isOpened={confirmingPause}>
        <ConfirmDialog
          closeModal={toggleConfirmingPause}
          onModalClosed={noop}
          message="Are you sure you want to pause this subscription?  Access to the Farm Bill search will be temporarily suspended while your pause is in effect."
          buttons={[
            { label: 'No', onClick: noop, className: 'btn-default' },
            {
              label: 'Yes',
              onClick: pauseSubscription,
              className: 'btn-danger'
            }
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      </Portal>
      <Portal isOpened={confirmingUnpause}>
        <ConfirmDialog
          closeModal={toggleConfirmingUnpause}
          onModalClosed={noop}
          message="Are you sure you want to unpause your subscription?  A monthly charge will automatically be applied to resume Farm Bill search services."
          buttons={[
            { label: 'No', onClick: noop, className: 'btn-default' },
            {
              label: 'Yes',
              onClick: unpauseSubscription,
              className: 'btn-danger'
            }
          ]}
          modalStyle={{ borderRadius: 3 }}
        />
      </Portal>
      {
        PAYROC_METHOD_AVAILABLE &&
        <PayrocGenericModal
          title="Select / Add Payment Method"
          isOpen={editingPaymentSource}
          onRequestClose={() => setEditingPaymentSource(false)}
          instructions="Select an existing payment method, or add a new payment method below."
          onPaymentSelected={setPaymentMethod}
        />
      }
    </div>
  )
}

const NicknameEditor = ({ nickname, onNicknameChange }) => {
  return (
    <div className="wizard nickname-editor">
      <DumbField label="Set a new nickname">
        <TextInput value={nickname} isFirst onChange={v => onNicknameChange(v)} />
      </DumbField>
    </div>
  )
}



// WEBPACK FOOTER //
// ./src/components/DataRequests/ManageDrawer.js